import { useEffect, useRef, useState } from 'react';
import { AudioLines, Loader2, RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import { DualSoundtrackCompare } from '@/components/reelsShorts/DualSoundtrackCompare';
import { ReelsShortsDialogueChat } from '@/components/reelsShorts/ReelsShortsDialogueChat';
import { snippetCleanSummary, snippetCleanToast, snippetAudioChoice, snippetOriginalAudioUrl, snippetCleanedAudioUrl, stampedAudioSrc } from '@/lib/reelsShortsAudioClean';

function snippetLocked(snippet) {
  const text = String(snippet?.transcript || '').trim();
  if (text) return false;
  const st = String(snippet?.status || '').toLowerCase();
  return ['uploaded', 'splitting', 'transcribing', 'pending'].includes(st);
}

function snippetAudioSrc(url, snippet) {
  const base = mediaUrlForPlayback(url);
  if (!base) return '';
  return stampedAudioSrc(base, snippet.audio_cleaned_at || snippet.updated_at || '');
}

export function ReelsShortsTranscriptPanel({
  projectId,
  snippets = [],
  language = 'en',
  onUpdated,
  disabled = false,
}) {
  const [draft, setDraft] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [retranscribingId, setRetranscribingId] = useState(null);
  const [cleaningId, setCleaningId] = useState(null);
  const [choosingAudioId, setChoosingAudioId] = useState(null);
  const [tabById, setTabById] = useState({});
  const serverTextRef = useRef({});

  useEffect(() => {
    setDraft((prev) => {
      const next = { ...prev };
      for (const snip of snippets || []) {
        const incoming = snip.transcript || '';
        const lastServer = serverTextRef.current[snip.id];
        const local = prev[snip.id];
        const dirty = local !== undefined && lastServer !== undefined && local !== lastServer;
        if (!dirty) next[snip.id] = incoming;
        serverTextRef.current[snip.id] = incoming;
      }
      return next;
    });
  }, [snippets]);

  const cleanAudio = async (snippet) => {
    if (!projectId || !snippet?.id) return;
    setCleaningId(snippet.id);
    try {
      const { data } = await api.post(
        `/reels-shorts/projects/${projectId}/snippets/${snippet.id}/clean-audio`,
        { preset: 'auto' },
        { timeout: 600_000 },
      );
      onUpdated?.(data.project);
      const next = (data.project?.snippets || []).find((s) => s.id === snippet.id);
      toast.success(snippetCleanToast(next || snippet, snippet.filename || 'snippet'));
    } catch (err) {
      toast.error(formatApiError(err, 'Could not clean this snippet audio'));
    } finally {
      setCleaningId(null);
    }
  };

  const chooseAudio = async (snippet, audioChoice) => {
    if (!projectId || !snippet?.id || snippetAudioChoice(snippet) === audioChoice) return;
    setChoosingAudioId(snippet.id);
    try {
      const { data } = await api.patch(
        `/reels-shorts/projects/${projectId}/snippets/${snippet.id}/audio-choice`,
        { audio_choice: audioChoice },
      );
      onUpdated?.(data.project);
      toast.success(audioChoice === 'cleaned' ? 'Using cleaned soundtrack' : 'Using original soundtrack');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not switch soundtrack'));
    } finally {
      setChoosingAudioId(null);
    }
  };

  const retranscribe = async (snippet) => {
    if (!projectId || !snippet?.id) return;
    setRetranscribingId(snippet.id);
    try {
      const { data } = await api.post(
        `/reels-shorts/projects/${projectId}/snippets/${snippet.id}/retranscribe`,
        {},
        { timeout: 600_000 },
      );
      const next = (data.project?.snippets || []).find((s) => s.id === snippet.id);
      if (next) {
        serverTextRef.current[snippet.id] = next.transcript || '';
        setDraft((prev) => ({ ...prev, [snippet.id]: next.transcript || '' }));
      }
      onUpdated?.(data.project);
      if (next?.status === 'failed') {
        toast.error(next.error_message || 'Could not re-transcribe this snippet');
      } else {
        toast.success(`Re-transcribed ${snippet.filename || 'snippet'}`);
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Could not re-transcribe this snippet'));
    } finally {
      setRetranscribingId(null);
    }
  };

  const save = async (snippet) => {
    if (!projectId || !snippet?.id) return;
    setSavingId(snippet.id);
    try {
      const { data } = await api.patch(
        `/reels-shorts/projects/${projectId}/snippets/${snippet.id}/transcript`,
        { transcript: draft[snippet.id] ?? '' },
      );
      toast.success(`Saved ${snippet.filename || 'snippet'}`);
      onUpdated?.(data.project);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save transcript'));
    } finally {
      setSavingId(null);
    }
  };

  if (!snippets.length) {
    return (
      <p className="text-sm text-slate-500">Snippets will appear here after processing.</p>
    );
  }

  return (
    <div className="space-y-4" data-testid="reels-shorts-transcripts">
      {snippets.map((snip, idx) => {
        const videoSrc = mediaUrlForPlayback(snip.video_s3_url || snip.original_s3_url);
        const originalSrc = snippetAudioSrc(snippetOriginalAudioUrl(snip) || snip.audio_s3_url || snip.original_s3_url, snip);
        const cleanedSrc = snippetAudioSrc(snippetCleanedAudioUrl(snip), snip);
        const choice = snippetAudioChoice(snip);
        const dirty = (draft[snip.id] ?? '') !== (snip.transcript || '');
        const cleanSummary = snippetCleanSummary(snip);
        const audioBusy = cleaningId === snip.id || choosingAudioId === snip.id;
        const hasDialogue = Boolean(
          (snip.dialogue_turns && snip.dialogue_turns.length)
          || (snip.word_timestamps || []).some((w) => w?.speaker),
        );
        const tab = tabById[snip.id] || (hasDialogue ? 'dialogue' : 'transcript');
        return (
          <article
            key={snip.id}
            className="rounded-xl border border-[#1f2635] bg-[#12151d] p-4 space-y-3"
            data-testid={`reels-shorts-snippet-${snip.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-rose-300/80 font-mono">
                  Snippet {idx + 1}
                </p>
                <h3 className="text-sm font-semibold text-white truncate">
                  {snip.filename || 'Clip'}
                </h3>
                <p className="text-[11px] text-slate-500">
                  {snip.duration_seconds
                    ? `${Number(snip.duration_seconds).toFixed(1)}s`
                    : 'duration pending'}
                  {' · '}
                  {snip.status || 'uploaded'}
                  {snip.audio_cleaned ? (
                    <span className="text-emerald-400/90">
                      {' · '}
                      {choice === 'cleaned' ? 'using cleaned' : 'using original'}
                      {cleanSummary ? ` (${cleanSummary})` : ''}
                    </span>
                  ) : null}
                  {hasDialogue ? (
                    <span className="text-sky-300/90">
                      {' · '}
                      {(snip.dialogue_turns || []).length || 'speaker'} turns
                    </span>
                  ) : null}
                </p>
              </div>
              {snip.error_message ? (
                <span className="text-[11px] text-rose-400 max-w-[48%] text-right break-words">
                  {snip.error_message}
                </span>
              ) : null}
            </div>

            <div className="grid sm:grid-cols-[140px_1fr] gap-3">
              <div className="rounded-lg overflow-hidden bg-black aspect-[9/16] max-h-44">
                {videoSrc ? (
                  <video
                    src={videoSrc}
                    controls
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[11px] text-slate-600">
                    Video pending
                  </div>
                )}
              </div>
              <div className="space-y-2 min-w-0">
                <DualSoundtrackCompare
                  originalSrc={originalSrc}
                  cleanedSrc={cleanedSrc}
                  projectId={projectId}
                  snippetId={snip.id}
                  choice={choice}
                  onChoose={(next) => chooseAudio(snip, next)}
                  chooseDisabled={disabled || audioBusy || Boolean(retranscribingId) || Boolean(cleaningId)}
                  cleanSummary={cleanSummary}
                  choiceTestId={`reels-shorts-audio-choice-${snip.id}`}
                />

                <div
                  className="flex gap-1 rounded-lg border border-white/10 bg-black/20 p-0.5"
                  data-testid={`reels-shorts-snippet-tabs-${snip.id}`}
                >
                  {[
                    { id: 'dialogue', label: 'Dialogue (chat)' },
                    { id: 'transcript', label: 'Combined' },
                    { id: 'audio', label: 'Split A/V tracks' },
                  ].map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => setTabById((prev) => ({ ...prev, [snip.id]: row.id }))}
                      className={`flex-1 rounded-md px-2 py-1.5 text-[10px] font-medium transition ${
                        tab === row.id
                          ? 'bg-rose-500/20 text-rose-100 border border-rose-400/30'
                          : 'text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      {row.label}
                    </button>
                  ))}
                </div>

                {tab === 'dialogue' ? (
                  <ReelsShortsDialogueChat snippet={snip} />
                ) : null}

                {tab === 'audio' ? (
                  <div
                    className="rounded-lg border border-white/10 bg-black/30 px-2.5 py-2 space-y-2"
                    data-testid={`reels-shorts-speaker-tracks-${snip.id}`}
                  >
                    <p className="text-[10px] uppercase tracking-widest text-emerald-300/90">
                      Split A/V audio
                      {snip.speaker_audio_method ? ` · ${snip.speaker_audio_method}` : ''}
                      {snip.audio_cleaned ? ' · cleaned' : ''}
                    </p>
                    {(snip.audio_mixed_s3_url || snip.source_audio_s3_url || snip.audio_s3_url) ? (
                      <div className="space-y-0.5">
                        <p className="text-[10px] text-slate-400">Combined (mixed)</p>
                        <audio
                          controls
                          preload="metadata"
                          className="w-full h-8"
                          src={snippetAudioSrc(
                            snip.audio_mixed_s3_url || snip.source_audio_s3_url || snip.audio_s3_url,
                            snip,
                          )}
                        />
                      </div>
                    ) : null}
                    {(snip.speaker_audio_tracks || []).map((track) => {
                      const href = snippetAudioSrc(track?.s3_url, snip);
                      if (!href) return null;
                      const side = String(track?.side || track?.id || 'speaker');
                      const label = track?.label || (side === 'left' ? 'Speaker A' : side === 'right' ? 'Speaker B' : side);
                      return (
                        <div key={track.s3_key || side} className="space-y-0.5">
                          <p className="text-[10px] text-slate-400">
                            Isolated · {label}
                            {track.method ? ` (${track.method})` : ''}
                          </p>
                          <audio controls preload="metadata" className="w-full h-8" src={href} />
                          {track.transcript ? (
                            <p className="text-[11px] text-slate-300 leading-relaxed">{track.transcript}</p>
                          ) : null}
                        </div>
                      );
                    })}
                    {!((snip.speaker_audio_tracks || []).length) ? (
                      <p className="text-[11px] text-slate-500">
                        No isolates yet — run Retry Split A/V.
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {tab === 'transcript' ? (
                  <>
                    {snip.transcript_dialogue ? (
                      <p className="text-[10px] text-slate-500">
                        Combined mix transcript — Dialogue (chat) tab shows speaker turns.
                      </p>
                    ) : null}
                    <Textarea
                      value={draft[snip.id] ?? ''}
                      onChange={(e) => setDraft((prev) => ({ ...prev, [snip.id]: e.target.value }))}
                      disabled={disabled || snippetLocked(snip) || savingId === snip.id || retranscribingId === snip.id || cleaningId === snip.id || choosingAudioId === snip.id}
                      dir="auto"
                      lang={language || undefined}
                      rows={5}
                      className="bg-black/40 border-[#2a3446] text-sm leading-relaxed [unicode-bidi:plaintext]"
                      placeholder="Combined spoken text from this snippet — type or paste in any language…"
                    />
                  </>
                ) : null}

                <div className="flex justify-end gap-2 flex-wrap">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={disabled || Boolean(retranscribingId) || Boolean(cleaningId) || Boolean(choosingAudioId) || savingId === snip.id}
                    onClick={() => cleanAudio(snip)}
                    data-testid={`reels-shorts-clean-audio-${snip.id}`}
                    title={snip.audio_cleaned ? 'Re-clean from the original — replaces the current cleaned soundtrack' : 'Clean this snippet audio'}
                  >
                    {cleaningId === snip.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                    ) : (
                      <AudioLines className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {snip.audio_cleaned ? 'Clean again' : 'Clean audio'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={disabled || Boolean(retranscribingId) || Boolean(cleaningId) || Boolean(choosingAudioId) || savingId === snip.id}
                    onClick={() => retranscribe(snip)}
                    data-testid={`reels-shorts-retranscribe-${snip.id}`}
                    title={choice === 'cleaned' ? 'Transcribe the cleaned soundtrack (currently selected)' : 'Transcribe the original soundtrack (currently selected)'}
                  >
                    {retranscribingId === snip.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Re-transcribe this
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={disabled || snippetLocked(snip) || !dirty || savingId === snip.id || retranscribingId === snip.id || cleaningId === snip.id || choosingAudioId === snip.id || tab !== 'transcript'}
                    onClick={() => save(snip)}
                  >
                    {savingId === snip.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                    ) : (
                      <Save className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Save transcript
                  </Button>
                </div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
